import React from "react";
import { Container, Grid, Typography, Box, Card, Chip, Stack } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SectionHeading from "./SectionHeading";
import useReveal from "./useReveal";
import { brand, gradients } from "../theme";

const tracks = [
  {
    title: "360° Placement Readiness",
    duration: "300 hrs",
    featured: true,
    frameworks: ["Aptitude", "DSA Bootcamp", "Technical Core", "Soft Skills"],
    outcome: "Industry-ready portfolios, measurable placement metrics & enhanced communication.",
  },
  {
    title: "Java Full Stack",
    duration: "120 hrs",
    frameworks: ["Spring Boot", "React", "Microservices", "Cloud Staging"],
    outcome: "Clear corporate algorithmic filters; deploy distributed enterprise apps.",
  },
  {
    title: "MERN Stack",
    duration: "100 hrs",
    frameworks: ["MongoDB", "Express", "React", "Node.js", "REST APIs"],
    outcome: "Design scalable, secure, token-authenticated full-stack web platforms.",
  },
  {
    title: "Python Full Stack",
    duration: "100 hrs",
    frameworks: ["Python", "Django", "FastAPI", "Web Layers"],
    outcome: "Build predictive modeling channels; handle large data ingestion streams.",
  },
  {
    title: "AI Full Stack Engineering",
    duration: "100 hrs",
    frameworks: ["Python", "GenAI Frameworks", "Full Stack Integration"],
    outcome: "Deploy custom cognitive tools integrated with responsive full-stack modules.",
  },
  {
    title: "Data Analytics",
    duration: "80 hrs",
    frameworks: ["Python", "Analysis Tools", "SQL Architecture"],
    outcome: "Formulate automated predictive dashboards & data pipelines.",
  },
  {
    title: "Cybersecurity",
    duration: "80 hrs",
    frameworks: ["Network Defense", "OWASP Top 10", "Pen-Testing Labs"],
    outcome: "Secure distributed endpoints & run comprehensive vulnerability audits.",
  },
  {
    title: "Cloud Computing",
    duration: "80 hrs",
    frameworks: ["AWS", "Docker", "Jenkins", "Linux"],
    outcome: "Configure end-to-end automated CI/CD deployment chains & clusters.",
  },
  {
    title: "Generative AI",
    duration: "60 hrs",
    frameworks: ["OpenAI APIs", "LLMs", "Prompt Design", "Vector Storage"],
    outcome: "Architect intelligent cognitive tools & specialized RAG platforms.",
  },
];

const TrainingPortfolio = () => {
  const ref = useReveal();
  return (
    <section id="portfolio-section" className="portfolio-section" ref={ref}>
      <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: "#fff" }}>
        <Container>
          <SectionHeading
            eyebrow="TRAINING PORTFOLIO"
            title="Outcome-Driven Curriculums"
            subtitle="Specialized, high-intensity technical tracks with clear timelines and workforce outcomes — every program shipping verifiable GitHub & Docker Hub artifacts."
          />
          <Grid container spacing={3} className="reveal-stagger">
            {tracks.map((t) => (
              <Grid item xs={12} sm={6} md={4} key={t.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    border: t.featured ? "none" : "1px solid rgba(12,131,200,0.12)",
                    background: t.featured ? gradients.brand : "#fff",
                    color: t.featured ? "#fff" : "inherit",
                    transition: "transform .3s ease, box-shadow .3s ease",
                    "&:hover": { transform: "translateY(-6px)", boxShadow: "0 20px 44px rgba(15,39,64,0.12)" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: t.featured ? "#fff" : brand.navy, fontSize: "1.05rem" }}
                    >
                      {t.title}
                    </Typography>
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: "16px !important", color: t.featured ? "#fff !important" : `${brand.blue} !important` }} />}
                      label={t.duration}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        fontWeight: 700,
                        color: t.featured ? "#fff" : brand.blueDark,
                        bgcolor: t.featured ? "rgba(255,255,255,0.2)" : "rgba(12,131,200,0.10)",
                      }}
                    />
                  </Box>
                  <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
                    {t.frameworks.map((f) => (
                      <Box
                        key={f}
                        sx={{
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 6,
                          fontSize: "0.74rem",
                          fontWeight: 600,
                          color: t.featured ? "#fff" : brand.slate,
                          bgcolor: t.featured ? "rgba(255,255,255,0.16)" : brand.bgAlt,
                        }}
                      >
                        {f}
                      </Box>
                    ))}
                  </Stack>
                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <CheckCircleIcon
                      sx={{ fontSize: 18, mt: "2px", color: t.featured ? "#fff" : brand.orange, flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: t.featured ? "rgba(255,255,255,0.92)" : brand.slate, fontSize: "0.85rem" }}
                    >
                      {t.outcome}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </section>
  );
};

export default TrainingPortfolio;
